import { apiHelper } from '@/utils'
import { API } from '@/constants'
import { buildListingParams } from './listing.params'

/**
 * Organisations — the brands whose players agents answer.
 *
 * Note the path: `/organizations`, not `/tenants`. `GET /tenants`
 * (tenant.service.js) is the agent's brand PICKER and returns a different
 * shape; this is the platform-staff management screen.
 *
 * There is no rename and no delete, and that is the API's shape rather than an
 * omission here: ai_bot owns the registry, its PATCH lists `name` as
 * operator-only, and conversations hang off tenant_id. See the backend's
 * OrganizationController.
 */
export const organizationService = {
  getAllOrganizations,
  getOrganization,
  getUsage,
  updateSettings,
  updateInboxSettings,
  getCredentials,
  saveCredentials,
  getCredentialProviders,
  getAllCredentials,
  getJkWebhook,
  rotateJkWebhook,
  createAppointmentForm,
  getAppointmentForm,
  saveAppointmentForm,
  createOrganization,
  getCalcomEventTypes,
  updateOrganizationStatus,
  getHealth,
  getCopy,
  saveCopy,
  translateCopy,
}

const API_URL = API.BASE_URL + '/v1'

function buildParams(filter) {
  return buildListingParams(filter, {
    name: filter?.name,
    is_active: filter?.isActive,
    tenant_id: filter?.tenantIds,
    // Opt-in: it costs one HTTP call to ai_bot, and the listing has to render
    // while the bot is restarting.
    with_bot: filter?.withBot ? 1 : undefined,
  })
}

export async function getAllOrganizations(filter) {
  return apiHelper(API_URL).get('/organizations', { params: buildParams(filter) })
}

export async function getOrganization(tenantId) {
  return apiHelper(API_URL).get(`/organizations/${tenantId}`)
}

export async function createOrganization(data) {
  return apiHelper(API_URL).post('/organizations', {
    name: data.name,
    /*
     * No tenant_id. ai_bot's `tenants.tenant_id` is AUTO_INCREMENT (its
     * migration 012), so the database assigns the brand number and it comes
     * back in the response. The API still accepts one, for importing a brand
     * that already has an id elsewhere — but that is not a thing this form
     * offers, because the number is meaningless to invent.
     */
    // Left out when blank so the server generates one. A human-chosen widget
    // token tends to be guessable and is never typed again after the snippet
    // is pasted.
    ...(data.botName ? { bot_name: data.botName } : {}),
    skills: data.skills && data.skills.length ? data.skills : undefined,
    // Checked by the server before the brand is written, so a refused endpoint
    // does not leave a half-made brand behind.
    ...(data.jkEndpoint ? { jk_endpoint: data.jkEndpoint } : {}),
    // Where bookings land, when the brand is created with Appointments on.
    ...(data.appointment ? { appointment: data.appointment } : {}),
  })
}

/** The platform Cal.com account and its event types: {data: [...], account}. */
export async function getCalcomEventTypes() {
  return apiHelper(API_URL).get('/organizations/calcom/event-types')
}

/** Install the standard appointment booking form. Answers {ready, fields}. */
export async function createAppointmentForm(tenantId) {
  return apiHelper(API_URL).post(`/organizations/${tenantId}/appointment-form`)
}

/** The booking form as stored: {ready, schema}. */
export async function getAppointmentForm(tenantId) {
  return apiHelper(API_URL).get(`/organizations/${tenantId}/appointment-form`)
}

/** Replace the booking form's questions. */
export async function saveAppointmentForm(tenantId, fields) {
  return apiHelper(API_URL).put(`/organizations/${tenantId}/appointment-form`, { fields })
}

export async function updateOrganizationStatus(tenantId, isActive) {
  return apiHelper(API_URL).patch(`/organizations/${tenantId}/status`, { is_active: isActive })
}


/**
 * The brand's own backend (JK today).
 *
 * The secret is one-way: ai_bot encrypts it and answers a masked hint, so what
 * comes back says WHICH key is installed, never what it is.
 */
export async function getCredentials(tenantId, provider = 'jk') {
  return apiHelper(API_URL).get(`/organizations/${tenantId}/credentials/${provider}`)
}

/** Omit `secret` to keep the stored one — the form shows a mask, and changing
 *  only the URL must not blank the token. */
export async function saveCredentials(tenantId, payload, provider = 'jk') {
  return apiHelper(API_URL).put(`/organizations/${tenantId}/credentials/${provider}`, payload)
}


/** The backends a brand can be wired to — ai_bot's registry, one card each. */
export async function getCredentialProviders() {
  return apiHelper(API_URL).get('/organizations/credential-providers')
}

/** Every provider for one brand in one call, configured or not. */
export async function getAllCredentials(tenantId) {
  return apiHelper(API_URL).get(`/organizations/${tenantId}/credentials`)
}

/** The brand's JK live-chat webhook: URL and its own secret, for the Install page. */
export async function getJkWebhook(tenantId) {
  return apiHelper(API_URL).get(`/organizations/${tenantId}/jk-webhook`)
}

/** Mint a new secret. The old one stops working at once; JK must be updated. */
export async function rotateJkWebhook(tenantId) {
  return apiHelper(API_URL).post(`/organizations/${tenantId}/jk-webhook/rotate`)
}

/** The month's turns, including what the console spent on this brand. */
export async function getUsage(tenantId, month) {
  return apiHelper(API_URL).get(`/organizations/${tenantId}/usage`, {
    params: month ? { month } : {},
  })
}

/** Skills, the unanswerable-question behaviour, and the included turns. */
export async function updateSettings(tenantId, payload) {
  return apiHelper(API_URL).patch(`/organizations/${tenantId}/settings`, payload)
}

/** Console-only presentation: inbox row colour per handover reason. */
export async function updateInboxSettings(tenantId, payload) {
  return apiHelper(API_URL).patch(`/organizations/${tenantId}/inbox-settings`, payload)
}

/**
 * Every dependency of a working bot, with a verdict each.
 *
 * One call rather than five. The facts were all knowable on 2026-10-06 and the
 * day still went into logs and SQL, because they lived on different screens
 * and got checked one at a time in the wrong order.
 */
export async function getHealth(tenantId) {
  return apiHelper(API_URL).get(`/organizations/${tenantId}/health`)
}

/** The four strings a brand owns, each with its wording AND the default. */
export async function getCopy(tenantId) {
  return apiHelper(API_URL).get(`/organizations/${tenantId}/copy`)
}

/** Blank means "use the platform default" — the server strips empties. */
export async function saveCopy(tenantId, copy) {
  return apiHelper(API_URL).put(`/organizations/${tenantId}/copy`, { copy })
}

/**
 * One line into the other languages, for review BEFORE saving.
 *
 * Not runtime translation: what gets saved is four fixed strings, so a customer
 * never waits on a model and the wording cannot drift between turns.
 *
 * `source` defaults to 'auto' — the bot sniffs which language was typed.
 * Asking an operator to declare it is a question with an obvious answer that
 * only they can get wrong.
 */
export async function translateCopy(tenantId, text, source = 'auto', targets) {
  return apiHelper(API_URL).post(`/organizations/${tenantId}/copy/translate`, {
    text,
    source,
    targets,
  })
}


